// src/pages/Dashboard.jsx
import { t } from '../lib/i18n';
import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, RadialBarChart, RadialBar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from 'recharts';
import { TrendingUp, TrendingDown, Target, Shield, Award, Flame, Eye, EyeOff, Link2, Hand } from 'lucide-react';
import { calcStats, getEquityCurveData, getPnlByDayData, getMistakeData } from '../utils/stats';
import { calcODFScore } from '../utils/odfScore';
import { generatePatternInsights } from '../utils/patternInsights';
import ODFScoreCard from '../components/ODFScoreCard';
import AIInsights from '../components/AIInsights';
import ProGate from '../components/ProGate';
import { usePlan } from '../hooks/usePlan';
import PatternInsights from '../components/PatternInsights';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const PRIVACY_KEY = 'odftrades_privacy';

function StatCard({ label, value, sub, color = '#f0f0f5', icon: Icon, hidden }) {
  return (
    <div className="stat-card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 11, color: '#8888a8', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Mono', monospace" }}>
          {label}
        </div>
        {Icon && <Icon size={15} style={{ color: '#2a2a3e' }} />}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, color: hidden ? '#2a2a3e' : color, fontFamily: "'DM Mono', monospace", letterSpacing: '-0.02em', filter: hidden ? 'blur(8px)' : 'none', userSelect: hidden ? 'none' : 'auto', transition: 'filter 0.2s' }}>
        {typeof value === 'number' ? value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#55556a' }}>{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#16161f', border: '1px solid #2a2a3e', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ color: '#8888a8', marginBottom: 4, fontFamily: "'DM Mono', monospace", fontSize: 10 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color || '#00e5a0', fontFamily: "'DM Mono', monospace" }}>
            {p.name}: {typeof p.value === 'number' ? (p.value >= 0 ? '+' : '') + p.value.toFixed(2) : p.value}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard({ trades, settings, shareableLink, lang = 'en' }) {
  const { user } = useAuth();
  const { isPro } = usePlan();
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('full_name, username').eq('id', user.id).single()
      .then(({ data }) => { if (data) setProfile(data); });
  }, [user]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };
  // Raw name from profile (username is forced lowercase in DB; first name is title-cased).
  // For the greeting we ALWAYS show with a leading capital — visual only, DB value is unchanged.
  const rawName     = profile?.username || profile?.full_name?.split(' ')[0] || 'Trader';
  const displayName = rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : 'Trader';
  const [privacy, setPrivacy] = useState(() => {
    try { return localStorage.getItem(PRIVACY_KEY) === 'true'; } catch { return false; }
  });

  const togglePrivacy = () => {
    const next = !privacy;
    setPrivacy(next);
    try { localStorage.setItem(PRIVACY_KEY, next); } catch {}
  };

  const safeSettings = settings || { startingBalance: 0 };
  const isEmpty    = trades.length === 0;
  const s          = calcStats(trades, safeSettings);
  const equityData = getEquityCurveData(trades, safeSettings.startingBalance || 0);
  const pnlData    = getPnlByDayData(trades);
  const mistakeData= getMistakeData(trades);
  const odfScore   = calcODFScore(trades, safeSettings);
  const patternData= generatePatternInsights(trades, safeSettings);

  // Session performance
  const sessionData = (() => {
    const sess = {};
    const closed = (trades||[]).filter(t => t.status !== 'open');
    for (const t of closed) {
      const s = t.session || 'Unknown';
      if (!sess[s]) sess[s] = { wins:0, total:0, pnl:0 };
      sess[s].total++;
      sess[s].pnl += Number(t.pnl||0);
      if (t.result === 'Win') sess[s].wins++;
    }
    return Object.entries(sess)
      .map(([session, d]) => ({ session, wr: Math.round(d.wins/d.total*100), total: d.total, pnl: Number(d.pnl.toFixed(2)) }))
      .sort((a,b) => b.total - a.total).slice(0, 5);
  })();

  // Pair performance
  const pairData = (() => {
    const p = {};
    const closed = trades.filter(t => t.status !== 'open');
    for (const t of closed) {
      if (!t.pair) continue;
      if (!p[t.pair]) p[t.pair] = { wins:0, total:0, pnl:0 };
      p[t.pair].total++;
      p[t.pair].pnl += Number(t.pnl||0);
      if (t.result === 'Win') p[t.pair].wins++;
    }
    return Object.entries(p)
      .map(([pair, d]) => ({ pair, wr: Math.round(d.wins/d.total*100), total: d.total, pnl: Number(d.pnl.toFixed(2)) }))
      .sort((a,b) => b.total - a.total).slice(0, 6);
  })();

  // Weekly P&L
  const weeklyData = (() => {
    const weeks = {};
    const closed = trades.filter(t => t.status !== 'open');
    for (const t of closed) {
      if (!t.date) continue;
      const d = new Date(t.date);
      const mon = new Date(d); mon.setDate(d.getDate() - d.getDay() + 1);
      const key = mon.toISOString().slice(0,10);
      if (!weeks[key]) weeks[key] = 0;
      weeks[key] += Number(t.pnl||0);
    }
    return Object.entries(weeks)
      .sort((a,b) => a[0].localeCompare(b[0]))
      .slice(-8)
      .map(([week, pnl]) => ({ week: week.slice(5), pnl: Number(pnl.toFixed(2)) }));
  })();

  const mask = (val) => privacy ? '••••••' : val;
  const maskNum = (val, prefix = '', suffix = '') => privacy
    ? <span style={{ letterSpacing: 2 }}>••••</span>
    : <>{prefix}{typeof val === 'number' ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val}{suffix}</>;

  return (
    <div className="fade-in" style={{ padding: '28px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: '#f0f0f5', fontFamily: "'IBM Plex Sans', sans-serif", margin: 0, letterSpacing: '-0.02em', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden style={{ fontSize: 22, lineHeight: 1 }}>👋</span>
              {greeting()}, {displayName}
            </h1>
            <span style={{ fontSize:10, color:'#00e5a0', background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:20, padding:'3px 10px', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em', textTransform:'uppercase' }}>
              Performance Summary
            </span>
          </div>
          <p style={{ color: '#55556a', fontSize: 13, margin: '3px 0 0', fontFamily: "'DM Mono', monospace" }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        {/* Share button */}
        <div style={{ display: 'flex', gap: 8 }}>
        {shareableLink && (
          <button onClick={() => { navigator.clipboard.writeText(shareableLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: copied ? 'rgba(0,229,160,0.1)' : 'transparent',
            border: `1px solid ${copied ? 'rgba(0,229,160,0.3)' : '#1e1e2e'}`,
            borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
            color: copied ? '#00e5a0' : '#8888a8', fontSize: 12,
            fontFamily: "'IBM Plex Sans', sans-serif", transition: 'all 0.2s', outline: 'none'
          }}>
            <Link2 size={13} /> {copied ? 'Copied!' : 'Share Dashboard'}
          </button>
        )}
        {/* Privacy Toggle */}
        <button
          onClick={togglePrivacy}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: privacy ? 'rgba(255,209,102,0.1)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${privacy ? 'rgba(255,209,102,0.3)' : '#1e1e2e'}`,
            borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
            color: privacy ? '#ffd166' : '#8888a8',
            fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif",
            transition: 'all 0.2s', outline: 'none'
          }}
        >
          {privacy ? <EyeOff size={14} /> : <Eye size={14} />}
          {privacy ? 'Privacy ON' : 'Privacy OFF'}
        </button>
        </div>
      </div>

      {/* Empty state for new users */}
      {isEmpty && (
        <div style={{ background:'rgba(0,229,160,0.05)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:12, padding:'20px 24px', marginBottom:20, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
          <div style={{ width:44, height:44, borderRadius:12, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', color:'#00e5a0', flexShrink:0 }}>
            <Hand size={20} strokeWidth={1.6} />
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5', marginBottom:4 }}>Welcome to ODFTrades</div>
            <div style={{ fontSize:13, color:'#8888a8', lineHeight:1.6 }}>
              Your dashboard is ready. Start by logging your first trade — your stats, equity curve, and performance data will appear here automatically.
            </div>
          </div>
          <div style={{ fontSize:12, color:'#00e5a0', fontFamily:"'DM Mono',monospace", whiteSpace:'nowrap' }}>
            ← Log first trade
          </div>
        </div>
      )}

      {/* Balance Card — privacy-aware, no full banner */}
      <div style={{
        background: '#16161f',
        border: `1px solid ${privacy ? '#1e1e2e' : '#2a2a3e'}`,
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
            Account Balance
          </div>
          <div style={{
            fontSize: 36, fontWeight: 600,
            color: s.currentBalance >= (settings.startingBalance || 0) ? '#00e5a0' : '#ff4d6d',
            fontFamily: "'DM Mono', monospace",
            letterSpacing: '-0.03em',
            filter: privacy ? 'blur(12px)' : 'none',
            transition: 'filter 0.25s',
            userSelect: privacy ? 'none' : 'auto'
          }}>
            ${s.currentBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: 12, color: '#55556a', marginTop: 4 }}>
            Started ${(settings.startingBalance || 0).toLocaleString()} &nbsp;·&nbsp;{' '}
            <span style={{
              color: s.totalPnl >= 0 ? '#00e5a0' : '#ff4d6d',
              filter: privacy ? 'blur(6px)' : 'none', transition: 'filter 0.25s'
            }}>
              {s.totalPnl >= 0 ? '+' : ''}${s.totalPnl.toFixed(2)} all time
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          {[
            { label: 'Today', val: s.dailyPnl },
            { label: 'This Week', val: s.weeklyPnl },
            { label: 'This Month', val: s.monthlyPnl },
          ].map(({ label, val }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{label}</div>
              <div style={{
                fontSize: 18, fontFamily: "'DM Mono', monospace", fontWeight: 500,
                color: val === 0 ? '#ffd166' : val > 0 ? '#00e5a0' : '#ff4d6d',
                filter: privacy ? 'blur(8px)' : 'none', transition: 'filter 0.25s'
              }}>
                {val >= 0 ? '+' : ''}${Math.abs(val).toFixed(2)}
                <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 3 }}>
                  ({val >= 0 ? '+' : ''}{((val / (settings.startingBalance || 0)) * 100).toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label={t(lang,'stat_roi')} value={`${s.roi >= 0 ? '+' : ''}${s.roi}%`} color={s.roi >= 0 ? '#00e5a0' : '#ff4d6d'} icon={TrendingUp} sub="Return on capital" hidden={privacy} />
        {odfScore && <ODFScoreCard score={odfScore} compact={true} />}

        <StatCard label={t(lang,'stat_winRate')} value={`${s.winRate}%`} color="#00e5a0" icon={Target} sub={`${s.wins}W / ${s.losses}L / ${s.breakevens}BE`} />
        <StatCard label={t(lang,'stat_totalTrades')} value={s.totalTrades} icon={Flame} sub="All time" />
        <StatCard label={t(lang,'stat_avgRR')} value={s.avgRR} color={s.avgRR >= 1 ? '#00e5a0' : '#ff4d6d'} icon={TrendingUp} sub="Per trade" />
        <StatCard label={t(lang,'stat_profitFactor')} value={s.profitFactor} color={parseFloat(s.profitFactor) >= 1.5 ? '#00e5a0' : parseFloat(s.profitFactor) >= 1 ? '#ffd166' : '#ff4d6d'} icon={Award} sub="Gross P / Gross L" />
        <StatCard label={t(lang,'stat_discipline')} value={`${s.ruleFollowPct}%`} color={s.ruleFollowPct >= 80 ? '#00e5a0' : s.ruleFollowPct >= 60 ? '#ffd166' : '#ff4d6d'} icon={Shield} sub="Rule follow rate" />
        <StatCard label={t(lang,'stat_maxDrawdown')} value={`$${s.maxDrawdown}`} color={s.maxDrawdown > 500 ? '#ff4d6d' : '#ffd166'} icon={TrendingDown} sub="All time" hidden={privacy} />
        <StatCard
          label={t(lang,'stat_bestDay')}
          value={s.bestDay ? `+$${s.bestDayPnl.toFixed(0)}` : '—'}
          color={s.bestDay ? '#00e5a0' : '#55556a'}
          sub={s.bestDay || 'No winning day yet'}
          hidden={privacy}
        />
        <StatCard
          label="Worst Day"
          value={s.worstDay ? `-$${Math.abs(s.worstDayPnl).toFixed(0)}` : '—'}
          color={s.worstDay ? '#ff4d6d' : '#55556a'}
          sub={s.worstDay || 'No losing day yet'}
          hidden={privacy}
        />
      </div>

      {/* Charts */}
      <div className="dashboard-charts" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="stat-card" style={{ padding: '20px 20px 8px' }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>Equity Curve</div>
            <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>Balance over time</div>
          </div>
          {equityData.length > 1 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={equityData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00e5a0" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#00e5a0" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" />
                <XAxis dataKey="date" tick={{ fill: '#55556a', fontSize: 9, fontFamily: "'DM Mono', monospace" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#55556a', fontSize: 9, fontFamily: "'DM Mono', monospace" }} tickLine={false} axisLine={false}
                  tickFormatter={v => privacy ? '••••' : `$${v.toLocaleString()}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="balance" name="Balance" stroke="#00e5a0" strokeWidth={2} fill="url(#eqGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#55556a', fontSize: 13 }}>
              Add more trades to see the curve
            </div>
          )}
        </div>

        <div className="stat-card" style={{ padding: '20px 20px 8px' }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>Daily P&L</div>
            <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>Per day</div>
          </div>
          {pnlData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={pnlData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" />
                <XAxis dataKey="date" tick={{ fill: '#55556a', fontSize: 9, fontFamily: "'DM Mono', monospace" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#55556a', fontSize: 9, fontFamily: "'DM Mono', monospace" }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
                  {pnlData.map((entry, index) => (
                    <Cell key={index} fill={entry.pnl >= 0 ? '#00e5a0' : '#ff4d6d'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#55556a', fontSize: 13 }}>No data yet</div>
          )}
        </div>
      </div>

      {/* ODF Score Full Card */}
      {odfScore && <ODFScoreCard score={odfScore} compact={false} />}

      {/* AI Insights + Pattern Insights — Pro gated */}
      <ProGate feature="AI Performance Coach" isPro={isPro}>
        <AIInsights trades={trades} settings={safeSettings} odfScore={odfScore} patternData={patternData} />
      </ProGate>
      <PatternInsights patternData={patternData} isPro={isPro} />

      {/* Session + Pair Performance */}
      {(sessionData.length > 0 || pairData.length > 0) && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }} className="dashboard-charts">
          {/* Session performance */}
          {sessionData.length > 0 && (
            <div className="stat-card" style={{ padding:'20px' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:4 }}>Session Performance</div>
              <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Win rate by session</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {sessionData.map((s,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:11, color:'#8888a8', minWidth:90, fontFamily:"'IBM Plex Sans',sans-serif" }}>{s.session}</span>
                    <div style={{ flex:1, background:'#1e1e2e', borderRadius:4, height:6, overflow:'hidden' }}>
                      <div style={{ width:`${s.wr}%`, height:'100%', background:s.wr>=60?'#00e5a0':s.wr>=40?'#ffd166':'#ff4d6d', borderRadius:4, transition:'width 0.5s ease' }} />
                    </div>
                    <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", minWidth:36, textAlign:'right', color:s.wr>=60?'#00e5a0':s.wr>=40?'#ffd166':'#ff4d6d', fontWeight:600 }}>{s.wr}%</span>
                    <span style={{ fontSize:10, color:'#55556a', minWidth:24 }}>{s.total}t</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pair performance */}
          {pairData.length > 0 && (
            <div className="stat-card" style={{ padding:'20px' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:4 }}>Pair Performance</div>
              <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Win rate by instrument</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {pairData.map((p,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:11, color:'#f0f0f5', fontWeight:500, minWidth:72 }}>{p.pair}</span>
                    <div style={{ flex:1, background:'#1e1e2e', borderRadius:4, height:6, overflow:'hidden' }}>
                      <div style={{ width:`${p.wr}%`, height:'100%', background:p.wr>=60?'#00e5a0':p.wr>=40?'#ffd166':'#ff4d6d', borderRadius:4, transition:'width 0.5s ease' }} />
                    </div>
                    <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", minWidth:36, textAlign:'right', color:p.wr>=60?'#00e5a0':p.wr>=40?'#ffd166':'#ff4d6d', fontWeight:600 }}>{p.wr}%</span>
                    <span style={{ fontSize:10, color:'#55556a', minWidth:24 }}>{p.total}t</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weekly P&L */}
      {weeklyData.length > 1 && (
        <div className="stat-card" style={{ padding:'20px 20px 8px', marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:4 }}>Weekly P&L</div>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Week-by-week summary</div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={weeklyData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" />
              <XAxis dataKey="week" tick={{ fill:'#55556a', fontSize:9, fontFamily:"'DM Mono',monospace" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill:'#55556a', fontSize:9 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="pnl" name="P&L" radius={[4,4,0,0]}>
                {weeklyData.map((entry,i) => <Cell key={i} fill={entry.pnl>=0?'#00e5a0':'#ff4d6d'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Mistakes */}
      {mistakeData.length > 0 && (
        <div className="stat-card fade-in" style={{ padding: 20 }}>
          <div style={{ marginBottom: 14, fontSize: 13, fontWeight: 600, color: '#f0f0f5' }}>Most Common Mistakes</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {mistakeData.map((m, i) => (
              <div key={i} style={{
                background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.2)',
                borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8
              }}>
                <span style={{ color: '#ff4d6d', fontWeight: 600, fontFamily: "'DM Mono', monospace", fontSize: 15 }}>{m.value}x</span>
                <span style={{ color: '#8888a8', fontSize: 13 }}>{m.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
